<template>
  <BaseCard
    title="Commission Summary"
    class="h-full"
  >
    <template #content>
      <div class="flex flex-col gap-4 min-h-fit">
        <div class="px-4">
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div v-if="isLoading">
              <Skeleton
                height="110px"
                width="100%"
              />
            </div>
            <div
              v-else
              class="bg-white border border-gray-200 p-6 rounded-md shadow-sm"
            >
              <BaseNumberRoll
                class="text-2xl font-bold text-primary"
                :end-val="summary.totalCommission.formatted"
                :prefix="summary.totalCommission.prefix"
                :suffix="summary.totalCommission.suffix"
                :decimal-places="summary.totalCommission.decimalPlaces"
              />
              <p class="text-sm text-gray-600 mt-1">
                Total Commission (All Time)
              </p>
            </div>

            <div v-if="isLoading">
              <Skeleton
                height="110px"
                width="100%"
              />
            </div>
            <div
              v-else
              class="bg-white border border-gray-200 p-6 rounded-md shadow-sm"
            >
              <BaseNumberRoll
                class="text-2xl font-bold text-primary"
                :end-val="summary.totalCommissionThisYear.formatted"
                :prefix="summary.totalCommissionThisYear.prefix"
                :suffix="summary.totalCommissionThisYear.suffix"
                :decimal-places="summary.totalCommissionThisYear.decimalPlaces"
              />
              <p class="text-sm text-gray-600 mt-1">
                Total Commission (This Year)
              </p>
            </div>

            <div v-if="isLoading">
              <Skeleton
                height="110px"
                width="100%"
              />
            </div>
            <div
              v-else
              class="bg-white border border-gray-200 p-6 rounded-md shadow-sm"
            >
              <BaseNumberRoll
                class="text-2xl font-bold text-primary"
                :end-val="summary.pendingCommission.formatted"
                :prefix="summary.pendingCommission.prefix"
                :suffix="summary.pendingCommission.suffix"
                :decimal-places="summary.pendingCommission.decimalPlaces"
              />
              <p class="text-sm text-gray-600 mt-1">
                Pending Commission
              </p>
            </div>

            <div v-if="isLoading">
              <Skeleton
                height="110px"
                width="100%"
              />
            </div>
            <div
              v-else
              class="bg-white border border-gray-200 p-6 rounded-md shadow-sm"
            >
              <BaseNumberRoll
                class="text-2xl font-bold text-primary"
                :end-val="summary.completedCommission.formatted"
                :prefix="summary.completedCommission.prefix"
                :suffix="summary.completedCommission.suffix"
                :decimal-places="summary.completedCommission.decimalPlaces"
              />
              <p class="text-sm text-gray-600 mt-1">
                Completed Commission
              </p>
            </div>
          </div>
        </div>
      </div>
    </template>
  </BaseCard>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useDashboard } from '~/composables/useDashboardCA'
import { useUtils } from '~/composables/useUtils'

interface CommissionSummary {
  totalCommission: {
    prefix: string
    original: number
    formatted: string
    suffix: string
    decimalPlaces: number
  }
  totalCommissionThisYear: {
    prefix: string
    original: number
    formatted: string
    suffix: string
    decimalPlaces: number
  }
  pendingCommission: {
    prefix: string
    original: number
    formatted: string
    suffix: string
    decimalPlaces: number
  }
  completedCommission: {
    prefix: string
    original: number
    formatted: string
    suffix: string
    decimalPlaces: number
  }
}

interface CommissionAnalyticsResponse {
  summary: {
    totalCommission: number
    totalCommissionThisYear: number
    totalCommissionThisMonth: number
    pendingCommission: number
    completedCommission: number
    averageCommissionPerDeal: number
    totalContracts: number
  }
  commissionByAgent: unknown[]
  commissionByMonth: unknown[]
  commissionByStage: unknown[]
  commissionByStatus: unknown[]
  officeVsAgentBreakdown: unknown
}

const { getCommissionAnalytics } = useDashboard()
const { numberRollFormatToCurrency } = useUtils()

const isLoading = ref(true)
const summary = ref<CommissionSummary>({
  totalCommission: {
    prefix: '',
    original: 0,
    formatted: '0',
    suffix: '',
    decimalPlaces: 0,
  },
  totalCommissionThisYear: {
    prefix: '',
    original: 0,
    formatted: '0',
    suffix: '',
    decimalPlaces: 0,
  },
  pendingCommission: {
    prefix: '',
    original: 0,
    formatted: '0',
    suffix: '',
    decimalPlaces: 0,
  },
  completedCommission: {
    prefix: '',
    original: 0,
    formatted: '0',
    suffix: '',
    decimalPlaces: 0,
  },
})

const getData = async () => {
  isLoading.value = true
  try {
    const response = await getCommissionAnalytics()
    const data = response?.data as CommissionAnalyticsResponse

    if (data) {
      const formatCommission = (value: number) => {
        const formatted = numberRollFormatToCurrency(value)
        // Ensure all required properties exist
        if ('prefix' in formatted && 'suffix' in formatted && 'decimalPlaces' in formatted) {
          return formatted
        }
        // Fallback if formatToCurrency returns partial object
        return {
          prefix: '$',
          original: value,
          formatted: value.toFixed(2),
          suffix: '',
          decimalPlaces: 2,
        }
      }

      summary.value = {
        totalCommission: formatCommission(data.summary.totalCommission),
        totalCommissionThisYear: formatCommission(data.summary.totalCommissionThisYear),
        pendingCommission: formatCommission(data.summary.pendingCommission),
        completedCommission: formatCommission(data.summary.completedCommission),
      }
    }
  }
  catch (error) {
    console.error('Error fetching commission analytics summary:', error)
    summary.value = {
      totalCommission: {
        prefix: '',
        original: 0,
        formatted: '0',
        suffix: '',
        decimalPlaces: 0,
      },
      totalCommissionThisYear: {
        prefix: '',
        original: 0,
        formatted: '0',
        suffix: '',
        decimalPlaces: 0,
      },
      pendingCommission: {
        prefix: '',
        original: 0,
        formatted: '0',
        suffix: '',
        decimalPlaces: 0,
      },
      completedCommission: {
        prefix: '',
        original: 0,
        formatted: '0',
        suffix: '',
        decimalPlaces: 0,
      },
    }
  }
  finally {
    isLoading.value = false
  }
}

onMounted(async () => {
  await getData()
})
</script>
